/**
 * LoadExecutionMobileCards
 * Mobile card view for Load Execution Detail section
 * Displays load dispatch data with selected metrics
 */

import React, { useCallback, useMemo, useState } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  Text,
  ActivityIndicator,
  useWindowDimensions,
  TouchableOpacity,
  Platform,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { FontFamily } from '../../../../../constants/fonts';
import FieldSelector, { FieldDefinition } from './FieldSelector';
import { useTheme } from '../../../../../theme/useTheme';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface LoadExecutionMobileRow {
  id: string;
  region: string;
  market: string;
  location: string;
  locationName?: string | null;
  locationType: string;
  driver: string;
  status: "Complete" | "Incomplete";
  onTime: boolean;
  arrivedAsExpected: "Y" | "N" | "Blank";
  deliveryDate: string;
  localRouteId: string;
  sequence?: string | null;
  nationalRouteId: string;
  manifestArrivalDateTime: string;
  actualArrivalDateTime: string;
  actualCompletedDateTime: string;
  palletCount?: number | null;
  percentComplete: number;
  vehicle: string;
  orderCount: number;
}

export interface LoadExecutionMobileCardsProps {
  rows: LoadExecutionMobileRow[];
  loading?: boolean;
  error?: string | null;
  onExpandCard?: (row: LoadExecutionMobileRow) => void;
  /**
   * When true, overrides dark mode regardless of the internal theme check.
   * Used when a parent component has already computed isDark for the mobile context.
   */
  isDark?: boolean;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const AVAILABLE_LOAD_EXECUTION_FIELDS: FieldDefinition[] = [
  { id: 'deliveryDate', label: 'Delivery Date', shortLabel: 'Date' },
  { id: 'location', label: 'Location', shortLabel: 'Loc' },
  { id: 'driver', label: 'Driver', shortLabel: 'Drv' },
  { id: 'localRouteId', label: 'Local Route ID + Sequence', shortLabel: 'Route' },
  { id: 'nationalRouteId', label: 'National Route ID', shortLabel: 'Nat Route' },
  { id: 'vehicle', label: 'Vehicle', shortLabel: 'Veh' },
  { id: 'orderCount', label: 'Order Count', shortLabel: 'Orders' },
  { id: 'palletCount', label: 'Pallet Count', shortLabel: 'Pallets' },
  { id: 'manifestArrivalDateTime', label: 'Manifest Arrival', shortLabel: 'Manifest' },
  { id: 'actualArrivalDateTime', label: 'Actual Arrival', shortLabel: 'Actual' },
  { id: 'percentComplete', label: 'Percent Complete', shortLabel: '%Complete' },
  { id: 'arrivedAsExpected', label: 'Arrived as Expected', shortLabel: 'Arrived' },
];

const DEFAULT_LOAD_EXECUTION_FIELDS = ['driver', 'vehicle', 'percentComplete'];

const formatLocalRouteWithSequence = (
  localRouteId: string,
  sequence?: string | null
): string => {
  const route = String(localRouteId ?? '').trim();
  const seq = String(sequence ?? '').trim();
  return seq ? `${route}+${seq}` : route;
};

// ─── Component ────────────────────────────────────────────────────────────────

const LoadExecutionMobileCards: React.FC<LoadExecutionMobileCardsProps> = ({
  rows,
  loading = false,
  error = null,
  onExpandCard,
  isDark: isDarkProp,
}) => {
  const { width } = useWindowDimensions();
  const { resolvedTheme, colors } = useTheme();
  const isMobilePhone = Platform.OS === 'ios' && width < 768;
  // isDark is true when prop says so (parent overrides), OR internal phone+dark detection
  const isDark = isDarkProp ?? (isMobilePhone && resolvedTheme === 'dark');

  const [isFieldSelectorExpanded, setIsFieldSelectorExpanded] = useState(false);
  const [selectedFields, setSelectedFields] = useState<string[]>(
    DEFAULT_LOAD_EXECUTION_FIELDS
  );
  const [appliedFields, setAppliedFields] = useState<string[]>(
    DEFAULT_LOAD_EXECUTION_FIELDS
  );

  // ─── Handlers ─────────────────────────────────────────────────────

  const handleToggleFieldSelector = useCallback(() => {
    setIsFieldSelectorExpanded(prev => !prev);
  }, []);

  const handleFieldToggle = useCallback((fieldId: string) => {
    setSelectedFields(prev => {
      if (prev.includes(fieldId)) {
        return prev.filter(id => id !== fieldId);
      }
      if (prev.length >= 3) {
        return prev;
      }
      return [...prev, fieldId];
    });
  }, []);

  const handleReset = useCallback(() => {
    setSelectedFields(DEFAULT_LOAD_EXECUTION_FIELDS);
  }, []);

  const handleApply = useCallback(() => {
    setAppliedFields(selectedFields);
    setIsFieldSelectorExpanded(false);
  }, [selectedFields]);

  // ─── Render Helpers ───────────────────────────────────────────────

  const getFieldValue = (row: LoadExecutionMobileRow, fieldId: string): string => {
    switch (fieldId) {
      case 'deliveryDate':
        return row.deliveryDate;
      case 'location':
        return row.locationName || row.location;
      case 'driver':
        return row.driver;
      case 'localRouteId':
        return formatLocalRouteWithSequence(row.localRouteId, row.sequence);
      case 'nationalRouteId':
        return row.nationalRouteId;
      case 'vehicle':
        return row.vehicle;
      case 'orderCount':
        return String(row.orderCount);
      case 'palletCount':
        return row.palletCount == null ? '-' : String(row.palletCount);
      case 'manifestArrivalDateTime':
        return row.manifestArrivalDateTime;
      case 'actualArrivalDateTime':
        return row.actualArrivalDateTime;
      case 'percentComplete':
        return `${row.percentComplete.toFixed(0)}%`;
      case 'arrivedAsExpected':
        return row.arrivedAsExpected === 'Blank' ? '-' : row.arrivedAsExpected;
      default:
        return '-';
    }
  };

  const renderCard = (row: LoadExecutionMobileRow, index: number) => {
    // Status badge colours — semantic meaning preserved in dark mode
    const statusColor = row.status === 'Complete'
      ? (isDark ? '#60B8E8' : '#01346F')
      : (isDark ? '#F87171' : '#C2410C');
    const statusBg = row.status === 'Complete'
      ? (isDark ? '#0D2A3E' : '#DAEEFA')
      : (isDark ? '#3A1414' : '#FEEDEC');
    const onTimeColor = row.onTime
      ? (isDark ? '#4ADE80' : '#0C420B')
      : (isDark ? '#F87171' : '#AB2B09');
    const onTimeBg = row.onTime
      ? (isDark ? '#14301A' : '#CAF3C6')
      : (isDark ? '#3A1414' : '#FFE5DE');

    return (
      <View
        key={`card-${index}`}
        style={[
          styles.card,
          isDark && {
            backgroundColor: colors.background.surface,
            borderColor: colors.border.default,
          },
        ]}
      >
        {/* Card Title - Dispatch ID (fixed, not selectable) */}
        <View
          style={[
            styles.cardHeader,
            isDark && {
              backgroundColor: colors.background.elevated,
              borderBottomColor: colors.border.divider,
            },
          ]}
        >
          <Text
            style={[styles.cardTitle, isDark && { color: colors.text.primary }]}
            numberOfLines={1}
          >
            {row.id}
          </Text>
          <Text style={[styles.cardSubtitle, isDark && { color: colors.text.secondary }]}>
            Dispatch ID
          </Text>
        </View>

        {/* Selected Fields */}
        <View style={styles.cardContent}>
          {appliedFields.map((fieldId) => {
            const field = AVAILABLE_LOAD_EXECUTION_FIELDS.find(f => f.id === fieldId);
            const value = getFieldValue(row, fieldId);
            return (
              <View key={`${index}-${fieldId}`} style={styles.fieldRow}>
                <Text style={[styles.fieldLabel, isDark && { color: colors.text.secondary }]}>
                  {field?.shortLabel || field?.label}
                </Text>
                <Text
                  style={[styles.fieldValue, isDark && { color: colors.text.primary }]}
                  numberOfLines={1}
                >
                  {value}
                </Text>
              </View>
            );
          })}
        </View>

        {/* Status Tags Footer */}
        <View
          style={[
            styles.cardFooter,
            isDark && {
              backgroundColor: colors.background.elevated,
              borderTopColor: colors.border.divider,
            },
          ]}
        >
          <View style={[styles.statusTag, { backgroundColor: statusBg }]}>
            <Text style={[styles.statusTagText, { color: statusColor }]}>
              {row.status}
            </Text>
          </View>
          <View style={[styles.onTimeTag, { backgroundColor: onTimeBg }]}>
            <Text style={[styles.onTimeTagText, { color: onTimeColor }]}>
              {row.onTime ? 'On Time' : 'Late'}
            </Text>
          </View>
          {row.percentComplete < 100 && (
            <View
              style={[
                styles.expandButton,
                isDark && { backgroundColor: colors.background.page },
              ]}
            >
              <MaterialIcons
                name="expand-more"
                size={18}
                color={isDark ? colors.brand.primary : '#0B5FC6'}
              />
            </View>
          )}
        </View>
      </View>
    );
  };

  if (loading) {
    return (
      <View
        style={[
          styles.centerContainer,
          isDark && {
            backgroundColor: colors.background.surface,
            borderColor: colors.border.default,
          },
        ]}
      >
        <ActivityIndicator size="large" color={isDark ? colors.brand.primary : '#005CBC'} />
        <Text style={[styles.loadingText, isDark && { color: colors.text.secondary }]}>
          Loading load execution...
        </Text>
      </View>
    );
  }

  if (error) {
    return (
      <View
        style={[
          styles.centerContainer,
          isDark && {
            backgroundColor: colors.background.surface,
            borderColor: colors.border.default,
          },
        ]}
      >
        <MaterialIcons name="error-outline" size={48} color="#C2410C" />
        <Text style={[styles.errorText, isDark && { color: '#F87171' }]}>{error}</Text>
      </View>
    );
  }

  if (rows.length === 0) {
    return (
      <View
        style={[
          styles.centerContainer,
          isDark && {
            backgroundColor: colors.background.surface,
            borderColor: colors.border.default,
          },
        ]}
      >
        <MaterialIcons
          name="info-outline"
          size={48}
          color={isDark ? colors.text.disabled : '#ADB5BD'}
        />
        <Text style={[styles.emptyText, isDark && { color: colors.text.secondary }]}>
          No load execution data available
        </Text>
      </View>
    );
  }

  return (
    <ScrollView
      style={[
        styles.container,
        isDark && { backgroundColor: colors.background.page },
      ]}
      contentContainerStyle={styles.scrollContent}
      showsVerticalScrollIndicator={false}
    >
      {/* Field Selector */}
      <FieldSelector
        isExpanded={isFieldSelectorExpanded}
        selectedFields={selectedFields}
        availableFields={AVAILABLE_LOAD_EXECUTION_FIELDS}
        maxFields={3}
        onToggle={handleToggleFieldSelector}
        onFieldToggle={handleFieldToggle}
        onReset={handleReset}
        onApply={handleApply}
        title="Load Execution Fields"
        isDark={isDark}
      />

      {/* Cards */}
      <View style={styles.cardsContainer}>
        {rows.map((row, index) => renderCard(row, index))}
      </View>
    </ScrollView>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },
  scrollContent: {
    paddingHorizontal: 14,
    paddingTop: 1,
    paddingBottom: 12,
  },
  centerContainer: {
    paddingVertical: 40,
    paddingHorizontal: 20,
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    marginHorizontal: 14,
    marginTop: 12,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    fontFamily: FontFamily.regular,
    color: '#6C757D',
  },
  errorText: {
    marginTop: 12,
    fontSize: 14,
    fontFamily: FontFamily.regular,
    color: '#C2410C',
    textAlign: 'center',
  },
  emptyText: {
    marginTop: 12,
    fontSize: 14,
    fontFamily: FontFamily.regular,
    color: '#6C757D',
  },
  cardsContainer: {
    gap: 12,
    marginTop: 12,
  },
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    overflow: 'hidden',
    shadowColor: '#A8B4C5',
    shadowOpacity: 0.08,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
  },
  cardHeader: {
    paddingHorizontal: 14,
    paddingVertical: 12,
    backgroundColor: '#F9FAFB',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  cardTitle: {
    fontSize: 15,
    fontFamily: FontFamily.bold,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 4,
  },
  cardSubtitle: {
    fontSize: 12,
    fontFamily: FontFamily.regular,
    color: '#6B7280',
  },
  cardContent: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    gap: 8,
  },
  fieldRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 4,
  },
  fieldLabel: {
    fontSize: 11,
    fontFamily: FontFamily.bold,
    color: '#667085',
    textTransform: 'uppercase',
    letterSpacing: 0.2,
  },
  fieldValue: {
    fontSize: 12,
    fontFamily: FontFamily.medium,
    color: '#111827',
    flex: 1,
    marginLeft: 8,
    textAlign: 'right',
  },
  cardFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 10,
    gap: 8,
    backgroundColor: '#F9FAFB',
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
  },
  statusTag: {
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 4,
    flex: 1,
  },
  statusTagText: {
    fontSize: 11,
    fontFamily: FontFamily.bold,
    fontWeight: '500',
    textAlign: 'center',
  },
  onTimeTag: {
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 4,
    flex: 1,
  },
  onTimeTagText: {
    fontSize: 11,
    fontFamily: FontFamily.bold,
    fontWeight: '500',
    textAlign: 'center',
  },
  expandButton: {
    width: 36,
    height: 32,
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#E9ECEF',
  },
});

LoadExecutionMobileCards.displayName = 'LoadExecutionMobileCards';

export default React.memo(LoadExecutionMobileCards);
